import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { badgeColorForNextTask, NextActionLine } from "components/dashboard/task-badge";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { defaultLinkStyle } from "components/ui/link";
import { Spinner } from "components/ui/spinner";
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
} from "lib/headlessui/components/menu/menu";
import React from "react";
import { Link } from "react-router-dom";
import { parsePhone } from "util/phone";
import { NO_NAME } from "util/text";
import { BoardStageCard } from "./types";

interface ReferralPipelineCardProps {
    card: BoardStageCard;
    highlightCard: boolean;
    pendingCard: boolean;
    onRemove: (card: BoardStageCard) => void;
}

export const ReferralPipelineCard: React.FC<ReferralPipelineCardProps> = props => {
    const { card, highlightCard, pendingCard, onRemove } = props;

    const detailUrl =
        card.entityType === "ReferenceContact"
            ? `/references/contacts/show/${card.entityId}`
            : card.entityType === "Community"
              ? `/communities/show/${card.entityId}`
              : `/references/organizations/show/${card.entityId}`;
    const phone = parsePhone(card.phone ?? "");

    const handleRemove = React.useCallback(async () => {
        const shouldRemove = await fancyConfirm(
            "Remove from pipeline",
            `Remove "${card.displayName || NO_NAME}" from this pipeline?`,
            "Yes, remove",
            "Cancel",
        );
        if (shouldRemove) {
            onRemove(card);
        }
    }, [onRemove, card]);

    return (
        <div
            className={`space-y-2 pipeline-card pipeline-client-card ${
                highlightCard ? "highlight-card" : ""
            }`}>
            <div className="flex gap-1 justify-between">
                <div className="flex-1">
                    <Link to={detailUrl} className={defaultLinkStyle}>
                        <div className="font-bold">{card.displayName || NO_NAME}</div>
                        {card.organizationName ? (
                            <div className="text-xs text-gray-600">
                                {card.organizationName}
                            </div>
                        ) : null}
                    </Link>
                </div>
                <div className="flex-none self-start flex items-center gap-1">
                    {pendingCard ? <Spinner /> : null}
                    <Menu as="div" className="relative">
                        <MenuButton
                            aria-label="Card actions"
                            className="block p-1 -mr-2 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500">
                            <EllipsisHorizontalIcon
                                className="w-5 h-5"
                                aria-hidden="true"
                            />
                        </MenuButton>
                        <MenuItems
                            anchor="bottom end"
                            className="z-1300 w-56 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none [--anchor-gap:0.25rem]">
                            <div className="py-1">
                                <MenuItem>
                                    {({ focus }) => (
                                        <div
                                            onClick={handleRemove}
                                            className={`${
                                                focus
                                                    ? "bg-brand-100 text-brand-900"
                                                    : "text-gray-700"
                                            } flex items-center px-4 py-2 text-sm cursor-pointer`}>
                                            Remove from pipeline
                                        </div>
                                    )}
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </Menu>
                </div>
            </div>
            {phone.digits !== "" ? (
                <div>
                    <a className={defaultLinkStyle} href={"tel:" + phone.digits}>
                        {phone.display}
                    </a>
                </div>
            ) : null}
            {card.email ? (
                <div className="truncate">
                    <a className={defaultLinkStyle} href={"mailto:" + card.email}>
                        {card.email}
                    </a>
                </div>
            ) : null}
            <div className="flex items-start justify-between gap-2">
                <NextActionLine nextTask={card.nextTask} />
                <div
                    className={`${badgeColorForNextTask(
                        card.nextTask,
                    )} rounded-full w-3 h-3 flex-none mt-1`}
                />
            </div>
            <div className="text-xs text-gray-600">
                Created {new Date(card.entityDateAdded).toLocaleDateString()}
            </div>
        </div>
    );
};
